import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'

import { DeleteModal } from '@makyoapp/makyo-ui'

import { useDbClientContext } from '@/context/__project__/DbClientContext'
import { useSingleProjectContext } from '@/context/SingleProjectContext'
import { useIsProjectDb } from '@/hooks/useIsProjectDb'
import { useParamId } from '@/hooks/useParamId'
import { ORG_DATABASE_ROUTE, ORG_ROUTE, PROJECT_ROUTE } from '@/shared/constants'
import { EVENT_KEYS } from '@/shared/custom-event'
import { useGridV2Context } from '@/components/GridV2/contexts/GridV2Context'

/**
 * @todo:
 *  use logic from DbClientContext and GridV2Context only
 */
export function DeleteTableModal() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const orgId = useParamId('org')
  const isProject = useIsProjectDb()
  const { project } = useSingleProjectContext()

  const { dbId, dbClient } = useDbClientContext()
  const { setTableToRemove, tableToRemove } = useGridV2Context()

  const [isLoading, setIsLoading] = useState(false)

  const handleDeleteTableAction = async () => {
    if (!dbClient || !tableToRemove) return

    setIsLoading(true)
    try {
      const nextTableId = await dbClient.DeleteTable(tableToRemove.id)

      // if (isProject) {
      //   await new Promise<void>((resolve) => {
      //     const onPageProcessFinished = () => {
      //       window.removeEventListener(EVENT_KEYS.PAGE_PROCESS_FINISHED, onPageProcessFinished)
      //       resolve()
      //     }
      //     window.addEventListener(EVENT_KEYS.PAGE_PROCESS_FINISHED, onPageProcessFinished)

      //     window.dispatchEvent(new CustomEvent(EVENT_KEYS.DB_TABLE_DELETED, { detail: tableToRemove.id }))
      //   })
      // }

      // redirect to next table id
      if (orgId) {
        navigate(`${ORG_ROUTE}/${orgId}/${ORG_DATABASE_ROUTE}/${dbId}/${nextTableId}`)
      } else if (isProject) {
        navigate(`${PROJECT_ROUTE}/${project?.id}/${ORG_DATABASE_ROUTE}/${nextTableId}`)
      }

      window.dispatchEvent(new CustomEvent('db-client-reload'))
      toast.success(t('Table deleted'))
      setTableToRemove(undefined)
      //
    } catch (error) {
      toast.error(t('Failed to delete table'))
    } finally {
      setIsLoading(false)
    }
  }

  const renderTitle = () => {
    if (tableToRemove?.isSnapshot) {
      return t('Delete Snapshot')
    }
    if (tableToRemove?.isQuery) {
      return t('Delete Query Table')
    }
    return t('Delete Table')
  }

  return tableToRemove ? (
    <DeleteModal
      id='delete-table'
      show={true}
      isLoading={isLoading}
      onHide={() => {
        setTableToRemove(undefined)
      }}
      continueLabel={t('Yes, delete')}
      onSubmit={handleDeleteTableAction}
      withCancel
      cancelLabel={t('Cancel')}
      onCancel={() => {
        setTableToRemove(undefined)
      }}
      title={renderTitle()}
    >
      <div className='modal-md'>
        {tableToRemove.isSnapshot ? (
          <>
            <div className='text-sm mb-2'>{t('Are you sure you want to delete this snapshot?')}</div>
            <div className='text-light'>
              {t("This won't affect the source table, and you can always create a new snapshot later.")}
            </div>
          </>
        ) : tableToRemove.isQuery ? (
          <>
            <div className='text-sm mb-2'>{t('Are you sure you want to delete this query table?')}</div>
            <div className='text-light'>
              {t("This won't affect the source table, and you can always create a new query table later.")}
            </div>
          </>
        ) : (
          <>
            <div className='text-sm mb-2'>{t("Are you sure you'd like to delete this table?")}</div>
            <div className='text-light'>
              {t('All the data will be deleted forever, you will not be able to restore it.')}
            </div>
          </>
        )}
      </div>
    </DeleteModal>
  ) : null
}
